export type BibleDefaults = {
  appLocale: string;
  versionId: string;
  supported: boolean;
};

// 已选圣经版本列表的持久化 key（ReadingPage 与 IntlProvider 共用）
export const VERSION_SELECTION_KEY = "inoneyear-selected-versions";

// 圣经版本语言 → 界面 locale：zh-hant 走繁体，其余直接使用语言代码
export const versionLanguageToLocale = (language?: string): string => {
  const norm = (language || "").toLowerCase();
  if (norm === "zh-hant" || norm === "zh-hk") return "zh-TW";
  if (norm === "zh") return "zh-CN";
  return norm || "en";
};

const normalize = (locale: string) => (locale || 'en').toLowerCase();

const supportsBibleLanguage = (lang: string) => {
  const supported = new Set(['zh', 'zh-hant', 'en', 'cs', 'da', 'de', 'el', 'es', 'fi', 'fr', 'hu', 'it', 'ja', 'ko', 'lt', 'lv', 'my', 'nl', 'no', 'pl', 'pt', 'ru', 'sl', 'sq', 'sr', 'sv', 'vi']);
  return supported.has(lang);
};

export const resolveBibleDefaults = (locale: string): BibleDefaults => {
  const normalized = normalize(locale);

  if (normalized.startsWith('zh')) {
    if (normalized.includes('tw') || normalized.includes('hk') || normalized.includes('hant')) {
      return { appLocale: 'zh-TW', versionId: 'ChiRcV', supported: true };
    }
    // inoneyear 没有版本选择器，中文默认用恢复注解版（含纲目/主题/注解）
    return { appLocale: 'zh', versionId: 'ChiRcVs', supported: true };
  }

  if (normalized.startsWith('en')) {
    return { appLocale: 'en', versionId: 'KJV', supported: true };
  }

  const base = normalized.split('-')[0];
  if (supportsBibleLanguage(base)) {
    return { appLocale: base, versionId: getSingleVersionByLanguage(base), supported: true };
  }

  return { appLocale: 'en', versionId: 'KJV', supported: false };
};

const getSingleVersionByLanguage = (language: string): string => {
  const map: Record<string, string> = {
    cs: 'CzeBKR', da: 'DaOT1871NT1907', de: 'GerElb1905', el: 'GreVamvas', es: 'SpaRV', fi: 'FinPR', fr: 'FreSegond1910', hu: 'HunKar', it: 'ItaRive', ja: 'JapKougo', ko: 'KorRV', lt: 'LtKBB', lv: 'LvGluck8', my: 'BurJudson', nl: 'DutSVV', no: 'Norsk', pl: 'PolUGdanska', pt: 'PorNVA', ru: 'RusSynodal', sl: 'SloChraska', sq: 'Alb', sr: 'SloChraska', sv: 'SweFolk', vi: 'Viet', zh: 'ChiUns', 'zh-hant': 'ChiUn', en: 'KJV',
  };
  return map[language] || 'KJV';
};

export const getDefaultVersionId = (locale: string): string => resolveBibleDefaults(locale).versionId;
